// src/pages/Login.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { Btn, Input, Field } from "../components/UI";
import BrandLogo from "../components/BrandLogo";

export default function Login() {
  const { loginEmail, loginGoogle, resetPassword, user, role, loading, profileError } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("designer"); // "designer" | "admin"
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetMsg, setResetMsg] = useState("");
  const [postLoginNav, setPostLoginNav] = useState(null); // "admin" | "portal" after credential OK

  const handleForgot = async () => {
    setError("");
    setResetMsg("");
    const em = email.trim();
    if (!em) {
      setError("Enter your email address above, then click Forgot password.");
      return;
    }
    setBusy(true);
    try {
      await resetPassword(em);
      setResetMsg("Check your inbox for a reset link.");
    } catch (err) {
      setError(err.message || "Could not send reset email.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (postLoginNav == null || loading) return;
    if (!user) {
      setPostLoginNav(null);
      return;
    }
    const wantAdmin = postLoginNav === "admin";
    setPostLoginNav(null);

    if (role === "admin") {
      navigate("/admin");
      return;
    }
    if (role === "designer" || role === "contractor") {
      if (wantAdmin) {
        setError("This account is not registered as showroom admin. Use the Designer / Contractor tab.");
      } else {
        navigate("/portal");
      }
      return;
    }
    setError(
      profileError ||
        "No portal profile found: in Firebase, open Authentication → Users, copy this account’s User UID, and confirm a document exists at designers/{that UID} or contractors/{that UID}. Or register again / ask an admin to add you."
    );
  }, [postLoginNav, loading, user, role, navigate, profileError]);

  const handleEmail = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await loginEmail(email, pass);
      setPostLoginNav(tab === "admin" ? "admin" : "portal");
    } catch (err) {
      setError(err.code === "auth/invalid-credential" ? "Invalid email or password." : err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = async () => {
    setError("");
    setBusy(true);
    try {
      await loginGoogle();
      setPostLoginNav(tab === "admin" ? "admin" : "portal");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", minHeight: "100vh" }}>
      <div
        style={{
          background: "var(--taupe)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          padding: 60,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ position: "absolute", top: -120, right: -120, width: 500, height: 500, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.06)" }} />
        <div style={{ position: "absolute", bottom: -80, left: -80, width: 320, height: 320, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.05)" }} />

        <div style={{ position: "relative", zIndex: 1 }}>
          <BrandLogo variant="light" style={{ maxWidth: 280 }} />
          <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.25em", textTransform: "uppercase", color: "rgba(255,255,255,0.45)", marginTop: 18 }}>
            Designer Program · Los Angeles
          </div>
        </div>

        <div style={{ position: "relative", zIndex: 1 }}>
          <h1 style={{ fontFamily: "'Bodoni Moda', serif", fontSize: "clamp(32px, 4vw, 48px)", fontWeight: 400, fontStyle: "italic", color: "#FFFFFF", lineHeight: 1.15, marginBottom: 16, letterSpacing: "-0.02em" }}>
            The Designer<br />Program
          </h1>
          <p style={{ fontSize: 13, color: "rgba(255,255,255,0.58)", lineHeight: 1.8, maxWidth: 340 }}>
            Earn commissions on orders you refer to our showroom. Track sales and payouts in one place.
          </p>
          <div style={{ marginTop: 28, display: "flex", gap: 28 }}>
            {[
              ["Tiered", "Commission"],
              ["Monthly", "Payouts"],
              ["Dedicated", "Support"],
            ].map(([v, l]) => (
              <div key={l}>
                <div style={{ fontFamily: "'Bodoni Moda', serif", fontSize: 24, fontWeight: 400, color: "rgba(255,255,255,0.85)" }}>{v}</div>
                <div style={{ fontSize: 9, letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(255,255,255,0.38)" }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ background: "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "center", padding: 60 }}>
        <div style={{ width: "100%", maxWidth: 380 }}>
          <div style={{ marginBottom: 20 }}>
            <BrandLogo variant="dark" style={{ maxWidth: 200 }} />
          </div>
          <h2 style={{ fontFamily: "'Bodoni Moda', serif", fontSize: 28, fontWeight: 400, marginBottom: 4, color: "var(--charcoal)" }}>Welcome back</h2>
          <p style={{ fontSize: 12, color: "var(--mid)", letterSpacing: "0.04em", marginBottom: 28 }}>Sign in to your account</p>

          <div style={{ display: "flex", border: "1px solid var(--border)", borderRadius: 2, overflow: "hidden", marginBottom: 24 }}>
            {["designer", "admin"].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                style={{
                  flex: 1,
                  padding: "10px",
                  fontFamily: "'Jost',sans-serif",
                  fontSize: 11,
                  fontWeight: 500,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  border: "none",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  background: tab === t ? "var(--taupe)" : "transparent",
                  color: tab === t ? "#FFFFFF" : "var(--mid)",
                }}
              >
                {t === "designer" ? "Designer / Contractor" : "Showroom Admin"}
              </button>
            ))}
          </div>

          {error && (
            <div style={{ background: "var(--red-light)", border: "1px solid rgba(140,48,48,0.2)", color: "var(--red)", padding: "10px 14px", borderRadius: 2, fontSize: 12, marginBottom: 16 }}>{error}</div>
          )}
          {resetMsg && (
            <div style={{ background: "var(--green-light)", border: "1px solid rgba(46,92,62,0.2)", color: "var(--green)", padding: "10px 14px", borderRadius: 2, fontSize: 12, marginBottom: 16 }}>{resetMsg}</div>
          )}

          <form onSubmit={handleEmail}>
            <Field label="Email Address">
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@firm.com" required disabled={busy || loading} />
            </Field>
            <Field label="Password">
              <Input type="password" value={pass} onChange={(e) => setPass(e.target.value)} placeholder="••••••••" required disabled={busy || loading} />
            </Field>
            <Btn type="submit" variant="primary" size="lg" fullWidth disabled={busy || loading}>
              {busy || loading ? "Signing in…" : "Sign In"}
            </Btn>
          </form>

          <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "18px 0" }}>
            <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
            <span style={{ fontSize: 11, color: "var(--light)", letterSpacing: "0.1em" }}>OR</span>
            <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
          </div>

          <button
            type="button"
            onClick={handleGoogle}
            disabled={busy || loading}
            style={{
              width: "100%",
              padding: "12px",
              border: "1px solid var(--border)",
              borderRadius: 2,
              background: "transparent",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              fontFamily: "'Jost',sans-serif",
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              cursor: "pointer",
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--cream)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
            }}
          >
            <svg width="16" height="16" viewBox="0 0 18 18">
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" />
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" />
              <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" />
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
            </svg>
            Continue with Google
          </button>

          <p style={{ marginTop: 16, fontSize: 11, color: "var(--light)", textAlign: "center" }}>
            <span role="button" tabIndex={0} onClick={handleForgot} onKeyDown={(e) => e.key === "Enter" && handleForgot()} style={{ color: "var(--gold)", cursor: "pointer" }}>
              Forgot password?
            </span>
            {tab === "designer" && (
              <>
                &nbsp;·&nbsp;
                <span style={{ color: "var(--gold)", cursor: "pointer" }} onClick={() => navigate("/register")}>
                  Apply to join
                </span>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
